import { useEffect, useState } from "react";
import { Copy, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Textarea } from "@/components/ui/Textarea";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useToast } from "@/hooks/useToast";
import { formatPLN } from "@/lib/format";
import { Invoice } from "@/types/billing";

interface Props {
  invoice: Invoice;
  senderEmail?: string;
  receiverEmail?: string;
  onSend?: () => void;
  onResend?: () => void;
  onSaveNote?: (message: string) => Promise<void>;
  sending?: boolean;
}

function statusBadge(status: Invoice["status"]) {
  if (status === "sent") return <Badge tone="success">Wysłano</Badge>;
  if (status === "finalized") return <Badge tone="info">Gotowe</Badge>;
  return <Badge tone="muted">Szkic</Badge>;
}

function fmtNum(v: string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = parseFloat(v);
  return isNaN(n) ? "—" : n.toFixed(2).replace(".", ",");
}

function prevPeriod(year: number, month: number): string {
  const m = month === 1 ? 12 : month - 1;
  const y = month === 1 ? year - 1 : year;
  return `${y}-${String(m).padStart(2, "0")}`;
}

function renderHtmlBody(invoice: Invoice): string {
  const chargedPeriod = prevPeriod(invoice.billing_year, invoice.billing_month);

  const headerRow = `
    <tr style="background:#f0f0f0">
      <th align="left"  style="padding:4px 8px">Opis</th>
      <th align="right" style="padding:4px 8px">Zużycie</th>
      <th align="right" style="padding:4px 8px">Na mieszk.</th>
      <th align="right" style="padding:4px 8px">Na osobę</th>
      <th align="right" style="padding:4px 8px">Do zapłaty</th>
    </tr>`;

  const rows = invoice.line_items.map((li) => `
    <tr>
      <td style="padding:4px 8px">${li.description}</td>
      <td align="right" style="padding:4px 8px;font-family:monospace">${fmtNum(li.usage)}</td>
      <td align="right" style="padding:4px 8px;font-family:monospace">${fmtNum(li.cost_per_flat)}</td>
      <td align="right" style="padding:4px 8px;font-family:monospace">${fmtNum(li.cost_per_person)}</td>
      <td align="right" style="padding:4px 8px;font-family:monospace">${formatPLN(li.amount)}</td>
    </tr>`).join("");

  const totalRow = `
    <tr style="border-top:2px solid #999;font-weight:bold">
      <td colspan="4" style="padding:4px 8px">Łącznie</td>
      <td align="right" style="padding:4px 8px;font-family:monospace">${formatPLN(invoice.total_amount)}</td>
    </tr>`;

  return `
<html><body style="font-family:sans-serif;font-size:14px;color:#222">
  <p>Dzień dobry,</p>
  <br/>
  <p>Przesyłam rozliczenie za okres ${chargedPeriod}:</p>
  <table border="1" cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%">
    ${headerRow}${rows}${totalRow}
  </table>
</body></html>`.trim();
}

export function EmailPreview({ invoice, senderEmail, receiverEmail, onSend, onResend, onSaveNote, sending }: Props) {
  const { push } = useToast();
  const [resendOpen, setResendOpen] = useState(false);
  const [note, setNote] = useState(invoice.notification_message ?? "");
  const [savingNote, setSavingNote] = useState(false);
  const chargedPeriod = prevPeriod(invoice.billing_year, invoice.billing_month);
  const subject = `Rozliczenie ${chargedPeriod}`;
  const sender = senderEmail ?? "aleksander.seman@gmail.com";
  const receiver = receiverEmail ?? "aleksander.seman@gmail.com";

  useEffect(() => {
    setNote(invoice.notification_message ?? "");
  }, [invoice.invoice_id, invoice.notification_message]);

  async function saveNote() {
    if (!onSaveNote || note === (invoice.notification_message ?? "")) return;
    setSavingNote(true);
    try {
      await onSaveNote(note);
    } catch (err) {
      push({ message: `Błąd zapisu notatki: ${(err as Error).message}`, tone: "danger" });
    } finally {
      setSavingNote(false);
    }
  }

  async function copy() {
    const text =
      `Od: ${sender}\nDo: ${receiver}\nTemat: ${subject}\n\n` +
      `Dzień dobry,\n` +
      `Przesyłam rozliczenie za okres ${prevPeriod(invoice.billing_year, invoice.billing_month)}:\n\n` +
      invoice.line_items
        .map((li) => [
          li.description,
          li.usage ?? "—",
          li.cost_per_flat ?? "—",
          li.cost_per_person ?? "—",
          formatPLN(li.amount),
        ].join("\t"))
        .join("\n") +
      `\n\nŁącznie: ${formatPLN(invoice.total_amount)}` +
      (note ? `\n\n${note}` : "") +
      `\n\nPozdrawiam,\nAleksander`;
    await navigator.clipboard.writeText(text);
    push({ message: "Skopiowano do schowka", tone: "success" });
  }

  return (
    <>
    <Card padding="none">
      <div className="px-4 py-3 border-b border-border bg-bg-subtle space-y-2">
        <div className="min-w-0 space-y-0.5 text-xs">
          <div className="flex gap-1"><span className="text-text-muted w-12 shrink-0">Od:</span> <span className="font-mono truncate">{sender}</span></div>
          <div className="flex gap-1"><span className="text-text-muted w-12 shrink-0">Do:</span> <span className="font-mono truncate">{invoice.tenant_email || "—"}</span></div>
          <div className="flex gap-1"><span className="text-text-muted w-12 shrink-0">CC:</span> <span className="font-mono truncate">{receiver}</span></div>
          <div className="flex gap-1"><span className="text-text-muted w-12 shrink-0">Temat:</span> <span className="font-medium text-text">{subject}</span></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {statusBadge(invoice.status)}
          <Button size="sm" variant="secondary" iconLeft={<Copy className="size-3.5" />} onClick={copy}>Skopiuj</Button>
          {onSend && invoice.status !== "sent" && (
            <Button size="sm" iconLeft={<Send className="size-3.5" />} onClick={onSend} loading={sending}>Wyślij</Button>
          )}
          {onResend && invoice.status === "sent" && (
            <Button size="sm" variant="secondary" iconLeft={<Send className="size-3.5" />} onClick={() => setResendOpen(true)}>Wyślij ponownie</Button>
          )}
        </div>
      </div>
      <div className="p-4 overflow-x-auto">
        <div className="prose prose-sm max-w-none text-text" dangerouslySetInnerHTML={{ __html: renderHtmlBody(invoice) }} />
        <div className="mt-3">
          <Textarea
            id="notification-message"
            label="Wytłumaczenie (opcjonalnie, widoczne w mailu)"
            placeholder="np. W tym miesiącu opłata za CO jest wyższa, bo..."
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={saveNote}
            disabled={savingNote}
          />
        </div>
        <p className="mt-3 text-sm text-text">Pozdrawiam,<br />Aleksander</p>
      </div>
      {invoice.status === "draft" && (
        <div className="px-4 py-2 border-t border-border text-xs text-text-muted flex items-center gap-1">
          <Mail className="size-3.5" />
          Faktura nie została jeszcze sfinalizowana — podgląd jest tymczasowy.
        </div>
      )}
    </Card>

    <ConfirmDialog
      open={resendOpen}
      onClose={() => setResendOpen(false)}
      onConfirm={() => { setResendOpen(false); onResend?.(); }}
      title="Wysłać ponownie?"
      confirmLabel="Wyślij"
      description="E-mail zostanie wysłany ponownie do tego samego odbiorcy."
    />
    </>
  );
}
